import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';

import CampaignModulesDataContext from 'bundles/campaigns/context/CampaignModulesDataContext';
import useIsPreview from 'bundles/campaigns/hooks/useIsPreview';
import GetCampaignModulesForCPlusLandingQuery from 'bundles/campaigns/queries/GetCampaignModulesForCPlusLandingQuery.graphql';
import type {
  GetCampaignModulesForCPlusLandingQuery as GetCampaignModulesForCPlusLandingQueryType,
  GetCampaignModulesForCPlusLandingQueryVariables,
} from 'bundles/campaigns/queries/__generated__/GetCampaignModulesForCPlusLandingQuery';
import { SentryErrorType, captureExceptionInSentry } from 'bundles/campaigns/utils/sentryUtil';
import shouldShowCampaigns from 'bundles/campaigns/utils/shouldShowCampaigns';

const CampaignModulesDataProviderForCPlusLanding = ({ children }: React.PropsWithChildren) => {
  const { query } = useLocation();
  const isPreview = useIsPreview();
  const campaignId = query.campaignId ?? null;

  const { data, loading } = useQuery<
    GetCampaignModulesForCPlusLandingQueryType,
    GetCampaignModulesForCPlusLandingQueryVariables
  >(GetCampaignModulesForCPlusLandingQuery, {
    context: {
      clientName: 'gatewayGql',
    },
    variables: {
      campaignModulesVariables: {
        useContentfulPreview: isPreview,
        forceUseCampaignId: campaignId,
      },
    },
    errorPolicy: 'all',
    ssr: true,
    skip: !shouldShowCampaigns(),
    onError: (error) => {
      captureExceptionInSentry({
        error,
        extra: {
          message: 'Error fetching campaign modules on CPlus Landing page',
          campaignId,
          isPreview,
        },
        errorType: SentryErrorType.GRAPHQL_ERROR,
      });
    },
  });

  let promoBarModuleData;

  const modules = data?.Campaign?.getCampaignModulesForCPlusLanding;
  if (modules?.promoBar?.__typename === 'Campaign_PromoBarModule') {
    promoBarModuleData = modules.promoBar;
  }

  return (
    <CampaignModulesDataContext.Provider
      value={{
        loading,
        campaignData: {
          promoBarModule: promoBarModuleData,
        },
      }}
    >
      {children}
    </CampaignModulesDataContext.Provider>
  );
};

export default CampaignModulesDataProviderForCPlusLanding;
